/**
 * NVIDIA Air DOT Export Module
 *
 * Generates DOT (Graphviz) format topology files for importing into NVIDIA Air
 * network simulation platform. Supports both InfiniBand and Ethernet fabrics.
 * Fully integrated with Phase 2 topology calculators (Fat Tree, Rail Optimized).
 *
 * DOT Format Reference: https://docs.nvidia.com/networking-ethernet-software/nvidia-air/Custom-Topology/
 *
 * Note: NVIDIA recommends using JSON format over DOT for new topologies,
 * but DOT remains supported for compatibility and visualization with Graphviz.
 */

import type { BillOfMaterials } from '../types/bom';
import type { ClusterConfiguration } from '../types/configuration';

/**
 * DOT Export Options
 */
export interface DOTExportOptions {
  includeComments?: boolean; // Include explanatory comments
  includeMetadata?: boolean; // Include topology metadata
  includeInfiniBand?: boolean; // Include IB fabric (default true)
  includeEthernet?: boolean; // Include Ethernet fabric (default true)
  includeOOB?: boolean; // Include OOB network (default true)
  osImage?: string; // Default OS image for switches
  computeOsImage?: string; // OS image for compute nodes
  maxNodesToShow?: number; // Maximum nodes to display (default 100)
  maxEdgesToShow?: number; // Maximum edges to display (default 500)
  filename?: string; // Output filename
  layoutDirection?: 'TB' | 'LR' | 'BT' | 'RL'; // Graph layout direction
  includePortLabels?: boolean; // Include port labels on connections
  colorByLayer?: boolean; // Color nodes by network layer
}

/**
 * Topology node for DOT export
 */
interface TopologyNode {
  id: string;
  type: 'compute' | 'leaf' | 'spine' | 'core' | 'oob';
  label: string;
  attributes: Record<string, string | number>;
}

/**
 * Topology edge for DOT export
 */
interface TopologyEdge {
  from: string;
  to: string;
  label?: string;
  attributes?: Record<string, string | number>;
}

/**
 * Escape DOT string (replace quotes and special chars)
 */
function escapeDOT(str: string): string {
  return str.replace(/"/g, '\\"').replace(/\n/g, '\\n');
}

/**
 * Format node attributes for DOT
 */
function formatNodeAttributes(node: TopologyNode, options: DOTExportOptions = {}): string {
  const attrs: string[] = [];

  // Add visual styling based on node type
  if (options.colorByLayer) {
    const colorMap: Record<string, string> = {
      compute: 'lightblue',
      leaf: 'lightgreen',
      spine: 'lightyellow',
      core: 'lightpink',
      oob: 'lightgray',
    };
    attrs.push(`fillcolor="${colorMap[node.type] || 'white'}"`);
    attrs.push('style="filled"');
  }

  // Add shape based on type
  const shapeMap: Record<string, string> = {
    compute: 'box',
    leaf: 'ellipse',
    spine: 'diamond',
    core: 'octagon',
    oob: 'hexagon',
  };
  attrs.push(`shape="${shapeMap[node.type] || 'box'}"`);

  // Add label
  attrs.push(`label="${escapeDOT(node.label)}"`);

  // Add custom attributes
  Object.entries(node.attributes).forEach(([key, value]) => {
    if (key !== 'label' && key !== 'shape' && key !== 'fillcolor' && key !== 'style') {
      attrs.push(`${key}="${escapeDOT(String(value))}"`);
    }
  });

  return attrs.length > 0 ? ` [${attrs.join(', ')}]` : '';
}

/**
 * Format edge attributes for DOT
 */
function formatEdgeAttributes(edge: TopologyEdge, options: DOTExportOptions = {}): string {
  if (!edge.attributes && !edge.label) {
    return '';
  }

  const attrs: string[] = [];

  if (edge.label && options.includePortLabels) {
    attrs.push(`label="${escapeDOT(edge.label)}"`);
  }

  if (edge.attributes) {
    Object.entries(edge.attributes).forEach(([key, value]) => {
      if (key !== 'label') {
        attrs.push(`${key}="${escapeDOT(String(value))}"`);
      }
    });
  }

  return attrs.length > 0 ? ` [${attrs.join(', ')}]` : '';
}

/**
 * Generate topology structure from BOM
 *
 * @param bom - Complete Bill of Materials
 * @param config - Configuration object
 * @param options - Export options
 * @returns Topology nodes and edges
 */
export function generateTopologyFromBOM(
  bom: BillOfMaterials,
  config?: ClusterConfiguration,
  options: DOTExportOptions = {}
): { nodes: TopologyNode[]; edges: TopologyEdge[] } {
  const {
    includeInfiniBand = true,
    includeEthernet = true,
    includeOOB = true,
    osImage = 'sonic-vs-202305',
    computeOsImage = 'generic/ubuntu2404',
    maxNodesToShow = 100,
    maxEdgesToShow = 500,
  } = options;

  const nodes: TopologyNode[] = [];
  const edges: TopologyEdge[] = [];

  const nodeCount = bom.summary.compute.totalNodes;
  const ewLeafCount = bom.summary.network.switchBreakdown.eastWestLeaf;
  const ewSpineCount = bom.summary.network.switchBreakdown.eastWestSpine;
  const nsLeafCount = bom.summary.network.switchBreakdown.northSouthLeaf;
  const nsSpineCount = bom.summary.network.switchBreakdown.northSouthSpine;
  const mgmtCount = bom.summary.network.switchBreakdown.management;

  // Extract port/link counts from configuration
  // Handle both new type-safe config and legacy UI config
  const ewConfig = (config as any)?.network?.eastWest;
  const nsConfig = (config as any)?.network?.northSouth;

  const ewLinksPerNode = ewConfig?.portsPerNode || ewConfig?.linksPerNode || 1;
  const nsLinksPerNode = nsConfig?.portsPerNode || nsConfig?.linksPerNode || 1;

  // Calculate uplinks and downlinks
  // For fat-tree, typically: downlinks used for nodes, uplinks to spine
  const ewLeafPorts = ewConfig?.leafSwitch?.ports || 64;
  const nsLeafPorts = nsConfig?.leafSwitch?.ports || 48;

  // Calculate downlinks per leaf (ports used to connect to nodes)
  // Typically this is: total leaf ports * downlink ratio
  const ewDownlinkRatio = ewConfig?.downlinkRatio || 0.5;
  const nsDownlinkRatio = nsConfig?.downlinkRatio || 0.5;

  const ewDownlinksPerLeaf = Math.floor(ewLeafPorts * ewDownlinkRatio);
  const nsDownlinksPerLeaf = Math.floor(nsLeafPorts * nsDownlinkRatio);

  // Calculate uplinks per leaf (ports used to connect to spine)
  const ewUplinksPerLeaf = ewLeafPorts - ewDownlinksPerLeaf;
  const nsUplinksPerLeaf = nsLeafPorts - nsDownlinksPerLeaf;

  // Debug logging - helps verify link counts match BOM calculations
  console.log('Topology Link Calculation:', {
    'Compute Nodes': {
      total: nodeCount,
      showing: Math.min(nodeCount, maxNodesToShow),
    },
    'InfiniBand (East-West)': {
      linksPerNode: ewLinksPerNode,
      leafSwitches: ewLeafCount,
      spineSwitches: ewSpineCount,
      leafPorts: ewLeafPorts,
      downlinksPerLeaf: ewDownlinksPerLeaf,
      uplinksPerLeaf: ewUplinksPerLeaf,
      downlinkRatio: ewDownlinkRatio,
    },
    'Ethernet (North-South)': {
      linksPerNode: nsLinksPerNode,
      leafSwitches: nsLeafCount,
      spineSwitches: nsSpineCount,
      leafPorts: nsLeafPorts,
      downlinksPerLeaf: nsDownlinksPerLeaf,
      uplinksPerLeaf: nsUplinksPerLeaf,
      downlinkRatio: nsDownlinkRatio,
    },
  });

  // Generate compute nodes (limited to maxNodesToShow)
  const nodesToGenerate = Math.min(nodeCount, maxNodesToShow);
  for (let i = 0; i < nodesToGenerate; i++) {
    const nodeId = `compute${String(i + 1).padStart(4, '0')}`;
    nodes.push({
      id: nodeId,
      type: 'compute',
      label: `Compute ${i + 1}`,
      attributes: {
        os: computeOsImage,
        function: 'server',
        gpu_model: bom.summary.compute.gpuModel,
        gpu_count: String(Math.floor(bom.summary.compute.totalGPUs / nodeCount)),
      },
    });
  }

  // Generate East/West InfiniBand switches
  if (includeInfiniBand) {
    // Leaf switches
    for (let i = 0; i < ewLeafCount; i++) {
      const switchId = `ib_leaf${String(i + 1).padStart(3, '0')}`;
      nodes.push({
        id: switchId,
        type: 'leaf',
        label: `IB Leaf ${i + 1}`,
        attributes: {
          os: osImage,
          function: 'leaf',
          fabric: 'infiniband',
          tier: 'leaf',
        },
      });
    }

    // Spine switches
    for (let i = 0; i < ewSpineCount; i++) {
      const switchId = `ib_spine${String(i + 1).padStart(3, '0')}`;
      nodes.push({
        id: switchId,
        type: 'spine',
        label: `IB Spine ${i + 1}`,
        attributes: {
          os: osImage,
          function: 'spine',
          fabric: 'infiniband',
          tier: 'spine',
        },
      });
    }

    // Connect compute nodes to IB leaf switches
    // Each node has ewLinksPerNode connections to leaf switches
    // IMPORTANT: Each link from a compute node connects to a DIFFERENT leaf switch
    let edgeCount = 0;
    for (let nodeIdx = 0; nodeIdx < nodesToGenerate && edgeCount < maxEdgesToShow; nodeIdx++) {
      const nodeId = `compute${String(nodeIdx + 1).padStart(4, '0')}`;

      // Create multiple links per node, each to a different leaf
      for (let linkIdx = 0; linkIdx < ewLinksPerNode && edgeCount < maxEdgesToShow; linkIdx++) {
        // Each link from this node goes to a different leaf switch
        // Round-robin distribution: node 0 link 0->leaf 0, link 1->leaf 1, etc.
        const leafIdx = (nodeIdx * ewLinksPerNode + linkIdx) % ewLeafCount;
        const leafId = `ib_leaf${String(leafIdx + 1).padStart(3, '0')}`;

        edges.push({
          from: nodeId,
          to: leafId,
          label: 'IB',
          attributes: {
            type: 'infiniband',
            speed: '400Gbps',
          },
        });
        edgeCount++;
      }
    }

    // Connect leaf to spine switches
    // Each leaf has ewUplinksPerLeaf connections to spine switches
    // These uplinks are distributed across all spine switches
    // NOTE: Don't limit leaf-to-spine by maxEdgesToShow - these are critical for topology correctness
    for (let leafIdx = 0; leafIdx < ewLeafCount; leafIdx++) {
      const leafId = `ib_leaf${String(leafIdx + 1).padStart(3, '0')}`;

      // Create all uplink connections from this leaf
      // In a fat-tree, these are distributed across spine switches
      for (let uplinkIdx = 0; uplinkIdx < ewUplinksPerLeaf; uplinkIdx++) {
        // Round-robin distribution across spine switches
        const spineIdx = uplinkIdx % ewSpineCount;
        const spineId = `ib_spine${String(spineIdx + 1).padStart(3, '0')}`;

        edges.push({
          from: leafId,
          to: spineId,
          label: 'IB',
          attributes: {
            type: 'infiniband',
            speed: '400Gbps',
            tier: 'leaf-to-spine',
          },
        });
        edgeCount++;
      }
    }

    console.log(`IB Topology: Created ${edgeCount} edges (${edgeCount - (nodesToGenerate * ewLinksPerNode)} leaf-to-spine)`);
  }

  // Generate North/South Ethernet switches
  if (includeEthernet) {
    // Leaf switches
    for (let i = 0; i < nsLeafCount; i++) {
      const switchId = `eth_leaf${String(i + 1).padStart(3, '0')}`;
      nodes.push({
        id: switchId,
        type: 'leaf',
        label: `Eth Leaf ${i + 1}`,
        attributes: {
          os: osImage,
          function: 'leaf',
          fabric: 'ethernet',
          tier: 'leaf',
        },
      });
    }

    // Spine switches
    for (let i = 0; i < nsSpineCount; i++) {
      const switchId = `eth_spine${String(i + 1).padStart(3, '0')}`;
      nodes.push({
        id: switchId,
        type: 'spine',
        label: `Eth Spine ${i + 1}`,
        attributes: {
          os: osImage,
          function: 'spine',
          fabric: 'ethernet',
          tier: 'spine',
        },
      });
    }

    // Connect compute nodes to Ethernet leaf switches
    // Each node has nsLinksPerNode connections to leaf switches
    // Note: Start fresh count for Ethernet, not cumulative from IB
    let ethEdgeCount = 0;
    for (let nodeIdx = 0; nodeIdx < nodesToGenerate && ethEdgeCount < maxEdgesToShow; nodeIdx++) {
      const nodeId = `compute${String(nodeIdx + 1).padStart(4, '0')}`;

      // Create multiple links per node, each to a different leaf
      for (let linkIdx = 0; linkIdx < nsLinksPerNode && ethEdgeCount < maxEdgesToShow; linkIdx++) {
        // Each link from this node goes to a different leaf switch
        // Round-robin distribution: node 0 link 0->leaf 0, link 1->leaf 1, etc.
        const leafIdx = (nodeIdx * nsLinksPerNode + linkIdx) % nsLeafCount;
        const leafId = `eth_leaf${String(leafIdx + 1).padStart(3, '0')}`;

        edges.push({
          from: nodeId,
          to: leafId,
          label: 'Eth',
          attributes: {
            type: 'ethernet',
            speed: '100Gbps',
          },
        });
        ethEdgeCount++;
      }
    }

    // Connect leaf to spine switches
    // Each leaf has nsUplinksPerLeaf connections to spine switches
    // These uplinks are distributed across all spine switches
    // NOTE: Don't limit leaf-to-spine by maxEdgesToShow - these are critical for topology correctness
    const nsEdgeCountBefore = ethEdgeCount;
    for (let leafIdx = 0; leafIdx < nsLeafCount; leafIdx++) {
      const leafId = `eth_leaf${String(leafIdx + 1).padStart(3, '0')}`;

      // Create all uplink connections from this leaf
      // In a fat-tree, these are distributed across spine switches
      for (let uplinkIdx = 0; uplinkIdx < nsUplinksPerLeaf; uplinkIdx++) {
        // Round-robin distribution across spine switches
        const spineIdx = uplinkIdx % nsSpineCount;
        const spineId = `eth_spine${String(spineIdx + 1).padStart(3, '0')}`;

        edges.push({
          from: leafId,
          to: spineId,
          label: 'Eth',
          attributes: {
            type: 'ethernet',
            speed: '100Gbps',
            tier: 'leaf-to-spine',
          },
        });
        ethEdgeCount++;
      }
    }

    const ethEdgesCreated = ethEdgeCount - nsEdgeCountBefore;
    console.log(`Ethernet Topology: Created ${ethEdgesCreated} leaf-to-spine edges`);
    console.log('Ethernet Edge Details:', {
      nsLeafCount,
      nsSpineCount,
      nsUplinksPerLeaf,
      nsLinksPerNode,
      nodesToGenerate,
      expectedNodeToLeaf: nodesToGenerate * nsLinksPerNode,
      expectedLeafToSpine: nsLeafCount * nsUplinksPerLeaf,
      actualLeafToSpine: ethEdgesCreated,
      totalEthernetEdges: ethEdgeCount,
    });
  }

  // Generate OOB management switches
  if (includeOOB && mgmtCount > 0) {
    for (let i = 0; i < mgmtCount; i++) {
      const switchId = `oob${String(i + 1).padStart(2, '0')}`;
      nodes.push({
        id: switchId,
        type: 'oob',
        label: `OOB ${i + 1}`,
        attributes: {
          os: osImage,
          function: 'management',
          fabric: 'oob',
        },
      });
    }

    // Connect subset of compute nodes to OOB (simplified)
    const oobSwitchId = 'oob01';
    let edgeCount = edges.length;
    for (let i = 0; i < Math.min(nodesToGenerate, 10) && edgeCount < maxEdgesToShow; i++) {
      const nodeId = `compute${String(i + 1).padStart(4, '0')}`;
      edges.push({
        from: nodeId,
        to: oobSwitchId,
        label: 'Mgmt',
        attributes: {
          type: 'management',
          speed: '1Gbps',
        },
      });
      edgeCount++;
    }
  }

  // Debug logging for edge counts
  const ibEdgeCount = edges.filter((e) => e.attributes?.type === 'infiniband').length;
  const ethEdgeCount = edges.filter((e) => e.attributes?.type === 'ethernet').length;
  const oobEdgeCount = edges.filter((e) => e.attributes?.type === 'management').length;

  console.log('Edge Generation Summary:', {
    totalEdges: edges.length,
    infiniBand: ibEdgeCount,
    ethernet: ethEdgeCount,
    oob: oobEdgeCount,
    includeInfiniBand,
    includeEthernet,
    includeOOB,
  });

  return { nodes, edges };
}

/**
 * Generate complete DOT file from BOM
 *
 * @param bom - Complete Bill of Materials
 * @param config - Configuration object
 * @param options - Export options
 * @returns DOT file content as string
 */
export function generateDOT(
  bom: BillOfMaterials,
  config?: ClusterConfiguration,
  options: DOTExportOptions = {}
): string {
  const {
    includeComments = true,
    includeMetadata = true,
    layoutDirection = 'TB',
    colorByLayer = true,
  } = options;

  const { nodes, edges } = generateTopologyFromBOM(bom, config, options);
  const lines: string[] = [];

  // Header comments
  if (includeComments && includeMetadata) {
    lines.push('/*');
    lines.push(` * ${bom.metadata.title}`);
    lines.push(` * BOM ID: ${bom.metadata.bomId}`);

    // Handle generatedAt which might be Date or string
    const generatedAtStr = bom.metadata.generatedAt instanceof Date
      ? bom.metadata.generatedAt.toISOString()
      : String(bom.metadata.generatedAt);
    lines.push(` * Generated: ${generatedAtStr}`);

    lines.push(` * Generator: GPU Cluster BOM Calculator v${bom.metadata.version}`);
    lines.push(' *');
    lines.push(' * NVIDIA Air Network Topology (DOT Format)');
    lines.push(' * Compatible with Graphviz and NVIDIA Air simulation platform');
    lines.push(' *');
    lines.push(` * Cluster Summary:`);
    lines.push(` *   - Total GPUs: ${bom.summary.compute.totalGPUs.toLocaleString()}`);
    lines.push(` *   - Compute Nodes: ${bom.summary.compute.totalNodes.toLocaleString()}`);
    lines.push(` *   - Total Switches: ${bom.summary.network.totalSwitches.toLocaleString()}`);
    lines.push(` *   - Bisection Bandwidth: ${bom.summary.network.bisectionBandwidthTbps.toFixed(1)} Tbps`);
    lines.push(' *');
    lines.push(' * Note: NVIDIA Air recommends JSON format for new topologies.');
    lines.push(' * This DOT file is provided for compatibility and visualization.');
    if (nodes.length < bom.summary.compute.totalNodes) {
      lines.push(` * WARNING: Showing ${nodes.filter((n) => n.type === 'compute').length} of ${bom.summary.compute.totalNodes} compute nodes for visualization`);
    }
    lines.push(' */');
    lines.push('');
  }

  // Graph declaration
  const graphName = bom.metadata.bomId.replace(/[^a-zA-Z0-9]/g, '_');
  lines.push(`graph "${graphName}" {`);
  lines.push('');

  // Graph attributes
  if (includeComments) {
    lines.push('  /* Graph Layout Attributes */');
  }
  lines.push(`  rankdir="${layoutDirection}";`);
  lines.push('  splines=line;  /* Direct straight lines */');
  lines.push('  nodesep=3.0;  /* Horizontal spacing between nodes */');
  lines.push('  ranksep=4.0;  /* Vertical spacing between tiers */');
  lines.push('  bgcolor="white";');
  lines.push('  newrank=true;  /* Enable rank constraints */');
  lines.push('  compound=true;  /* Allow edges between subgraphs */');
  lines.push('  center=true;  /* Center the graph */');
  lines.push('  ratio=auto;  /* Auto aspect ratio */');
  lines.push('  margin=0;  /* No margin */');
  lines.push('  pad="2,2";  /* Padding around graph */');
  lines.push('');

  console.log('DOT Graph attributes:', {
    rankdir: layoutDirection,
    splines: 'line',
    nodesep: 3.0,
    ranksep: 4.0,
    center: true,
    ratio: 'compress',
  });

  // Group nodes by type
  const computeNodes = nodes.filter((n) => n.type === 'compute');
  const leafNodes = nodes.filter((n) => n.type === 'leaf');
  const spineNodes = nodes.filter((n) => n.type === 'spine');
  const coreNodes = nodes.filter((n) => n.type === 'core');
  const oobNodes = nodes.filter((n) => n.type === 'oob');

  // Separate IB and Ethernet switches for clearer visualization
  const ibSpineNodes = spineNodes.filter((n) => n.attributes.fabric === 'infiniband');
  const ethSpineNodes = spineNodes.filter((n) => n.attributes.fabric === 'ethernet');
  const ibLeafNodes = leafNodes.filter((n) => n.attributes.fabric === 'infiniband');
  const ethLeafNodes = leafNodes.filter((n) => n.attributes.fabric === 'ethernet');

  // Tier 1: InfiniBand Spine switches (top tier)
  if (ibSpineNodes.length > 0 || coreNodes.length > 0) {
    if (includeComments) {
      lines.push('  /* Tier 1: InfiniBand Spine Layer - Top of Hierarchy */');
    }
    lines.push('  { rank=same;');

    if (ibSpineNodes.length > 0) {
      lines.push('    /* InfiniBand Spine Switches */');
      ibSpineNodes.forEach((node, idx) => {
        lines.push(`    "${node.id}"${formatNodeAttributes(node, options)};`);
      });
    }

    if (coreNodes.length > 0) {
      lines.push('    /* Core Switches */');
      coreNodes.forEach((node) => {
        lines.push(`    "${node.id}"${formatNodeAttributes(node, options)};`);
      });
    }

    lines.push('  }');
    lines.push('');
  }

  // Tier 2: InfiniBand Leaf switches
  if (ibLeafNodes.length > 0) {
    if (includeComments) {
      lines.push('  /* Tier 2: InfiniBand Leaf Layer */');
    }
    lines.push('  { rank=same;');
    lines.push('    /* InfiniBand Leaf Switches */');
    ibLeafNodes.forEach((node) => {
      lines.push(`    "${node.id}"${formatNodeAttributes(node, options)};`);
    });
    lines.push('  }');
    lines.push('');
  }

  // Tier 3: Compute nodes (middle tier)
  if (computeNodes.length > 0) {
    if (includeComments) {
      lines.push(`  /* Tier 3: Compute Nodes - Middle of Hierarchy (showing ${computeNodes.length} of ${bom.summary.compute.totalNodes}) */`);
    }
    lines.push('  { rank=same;');
    computeNodes.forEach((node) => {
      lines.push(`    "${node.id}"${formatNodeAttributes(node, options)};`);
    });
    lines.push('  }');
    lines.push('');
  }

  // Tier 4: Ethernet Leaf switches
  if (ethLeafNodes.length > 0) {
    if (includeComments) {
      lines.push('  /* Tier 4: Ethernet Leaf Layer */');
    }
    lines.push('  { rank=same;');
    lines.push('    /* Ethernet Leaf Switches */');
    ethLeafNodes.forEach((node) => {
      lines.push(`    "${node.id}"${formatNodeAttributes(node, options)};`);
    });
    lines.push('  }');
    lines.push('');
  }

  // Tier 5: Ethernet Spine switches (bottom tier)
  if (ethSpineNodes.length > 0) {
    if (includeComments) {
      lines.push('  /* Tier 5: Ethernet Spine Layer - Bottom of Hierarchy */');
    }
    lines.push('  { rank=same;');
    lines.push('    /* Ethernet Spine Switches */');
    ethSpineNodes.forEach((node) => {
      lines.push(`    "${node.id}"${formatNodeAttributes(node, options)};`);
    });
    lines.push('  }');
    lines.push('');
  }

  // OOB switches (separate from main hierarchy)
  if (oobNodes.length > 0) {
    if (includeComments) {
      lines.push('  /* Out-of-Band Management Switches (separate tier) */');
    }
    lines.push('  {');
    lines.push('    rank=min;');
    oobNodes.forEach((node) => {
      lines.push(`    "${node.id}"${formatNodeAttributes(node, options)};`);
    });
    lines.push('  }');
    lines.push('');
  }

  // Add invisible edges to enforce tier ordering
  if (includeComments) {
    lines.push('  /* Tier Ordering Constraints */');
  }
  // Create strong ordering constraints with invisible edges and high weight
  // InfiniBand fabric: Spine -> Leaf -> Compute (top to middle)
  if (ibSpineNodes.length > 0 && ibLeafNodes.length > 0) {
    lines.push(`  "${ibSpineNodes[0].id}" -- "${ibLeafNodes[0].id}" [style=invis, weight=100];`);
  }
  if (ibLeafNodes.length > 0 && computeNodes.length > 0) {
    lines.push(`  "${ibLeafNodes[0].id}" -- "${computeNodes[0].id}" [style=invis, weight=100];`);
  }
  // Ethernet fabric: Compute -> Leaf -> Spine (middle to bottom)
  if (computeNodes.length > 0 && ethLeafNodes.length > 0) {
    lines.push(`  "${computeNodes[0].id}" -- "${ethLeafNodes[0].id}" [style=invis, weight=100];`);
  }
  if (ethLeafNodes.length > 0 && ethSpineNodes.length > 0) {
    lines.push(`  "${ethLeafNodes[0].id}" -- "${ethSpineNodes[0].id}" [style=invis, weight=100];`);
  }
  lines.push('');

  // Edges/Connections
  if (edges.length > 0) {
    if (includeComments) {
      lines.push(`  /* Network Connections (showing ${edges.length} connections) */`);
      lines.push('');
    }

    // Group edges by type
    const ibEdges = edges.filter((e) => e.attributes?.type === 'infiniband');
    const ethEdges = edges.filter((e) => e.attributes?.type === 'ethernet');
    const oobEdges = edges.filter((e) => e.attributes?.type === 'management');

    if (ibEdges.length > 0) {
      if (includeComments) {
        lines.push(`  /* InfiniBand Connections (${ibEdges.length}) */`);
      }
      ibEdges.forEach((edge) => {
        // constraint=false prevents these edges from affecting rank ordering
        const attrs = formatEdgeAttributes(edge, options);
        const attrsWithConstraint = attrs
          ? attrs.replace('[', '[constraint=false, color=blue, penwidth=2, ')
          : ' [constraint=false, color=blue, penwidth=2]';
        lines.push(`  "${edge.from}" -- "${edge.to}"${attrsWithConstraint};`);
      });
      lines.push('');
    }

    if (ethEdges.length > 0) {
      if (includeComments) {
        lines.push(`  /* Ethernet Connections (${ethEdges.length}) */`);
      }
      console.log(`Rendering ${ethEdges.length} Ethernet edges in DOT output`);
      console.log('Sample Ethernet edges:', ethEdges.slice(0, 5));
      ethEdges.forEach((edge, idx) => {
        // constraint=false prevents these edges from affecting rank ordering
        const attrs = formatEdgeAttributes(edge, options);
        const attrsWithConstraint = attrs
          ? attrs.replace('[', '[constraint=false, color=green, penwidth=2, ')
          : ' [constraint=false, color=green, penwidth=2]';
        const edgeLine = `  "${edge.from}" -- "${edge.to}"${attrsWithConstraint};`;
        if (idx < 3) console.log('Ethernet edge line:', edgeLine);
        lines.push(edgeLine);
      });
      lines.push('');
    } else {
      console.warn('No Ethernet edges to render!', {
        totalEdges: edges.length,
        filteredEthernet: ethEdges.length,
        allEdgeTypes: edges.map(e => e.attributes?.type),
      });
    }

    if (oobEdges.length > 0) {
      if (includeComments) {
        lines.push(`  /* Management Connections (${oobEdges.length}) */`);
      }
      oobEdges.forEach((edge) => {
        // constraint=false prevents these edges from affecting rank ordering
        const attrs = formatEdgeAttributes(edge, options);
        const attrsWithConstraint = attrs
          ? attrs.replace('[', '[constraint=false, color=gray, penwidth=1, style=dashed, ')
          : ' [constraint=false, color=gray, penwidth=1, style=dashed]';
        lines.push(`  "${edge.from}" -- "${edge.to}"${attrsWithConstraint};`);
      });
      lines.push('');
    }
  }

  // Legend
  if (includeComments && colorByLayer) {
    lines.push('  /* Legend */');
    lines.push('  subgraph cluster_legend {');
    lines.push('    label="Legend";');
    lines.push('    style="dashed";');
    lines.push('    legend_compute [label="Compute Node", shape="box", fillcolor="lightblue", style="filled"];');
    lines.push('    legend_leaf [label="Leaf Switch", shape="ellipse", fillcolor="lightgreen", style="filled"];');
    lines.push('    legend_spine [label="Spine Switch", shape="diamond", fillcolor="lightyellow", style="filled"];');
    lines.push('    legend_oob [label="Management", shape="hexagon", fillcolor="lightgray", style="filled"];');
    lines.push('  }');
    lines.push('');
  }

  // Close graph
  lines.push('}');
  lines.push('');

  return lines.join('\n');
}

/**
 * Download DOT file to browser
 *
 * @param bom - Complete Bill of Materials
 * @param config - Configuration object
 * @param filename - Output filename
 * @param options - Export options
 */
export function downloadDOT(
  bom: BillOfMaterials,
  config?: ClusterConfiguration,
  filename?: string,
  options: DOTExportOptions = {}
): void {
  const dot = generateDOT(bom, config, options);
  const finalFilename = filename || options.filename || `gpu-cluster-topology-${bom.metadata.bomId}.dot`;
  const blob = new Blob([dot], { type: 'text/vnd.graphviz;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', finalFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Validate DOT syntax (basic validation)
 *
 * @param dotContent - DOT file content
 * @returns Validation result with any errors
 */
export function validateDOT(dotContent: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check for graph declaration
  if (!dotContent.includes('graph ')) {
    errors.push('Missing graph declaration');
  }

  // Check for balanced braces
  const openBraces = (dotContent.match(/{/g) || []).length;
  const closeBraces = (dotContent.match(/}/g) || []).length;
  if (openBraces !== closeBraces) {
    errors.push(`Unbalanced braces: ${openBraces} open, ${closeBraces} close`);
  }

  // Check for node declarations (should have at least one)
  const nodePattern = /"[\w-]+"(\s*\[.*?\])?;/g;
  const nodes = dotContent.match(nodePattern);
  if (!nodes || nodes.length === 0) {
    errors.push('No node declarations found');
  }

  // Check for edge declarations
  const edgePattern = /"[\w-]+"\s+--\s+"[\w-]+"/g;
  const edges = dotContent.match(edgePattern);
  if (!edges || edges.length === 0) {
    errors.push('Warning: No edge declarations found (graph may be empty)');
  }

  return {
    valid: errors.filter((e) => !e.startsWith('Warning:')).length === 0,
    errors,
  };
}
